import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Ref } from "shared/types/Ref"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { Button, ButtonProps } from "../Button"

interface SplitButtonProps extends React.HTMLProps<HTMLDivElement> {
  variant?: string
  isDisabled?: boolean

  primaryButtonRef?: Ref<HTMLButtonElement>
  primaryButtonLabel: string
  primaryButtonProps?: ButtonProps
  onPrimaryClick?: () => void

  secondaryButtonRef?: Ref<HTMLButtonElement>
  secondaryButtonLabel?: string
  secondaryButtonIcon?: IconName
  secondaryButtonProps?: ButtonProps
  secondaryButtonRender?: (props: ButtonProps) => React.ReactNode
  onSecondaryClick?: () => void

  containerRef?: Ref<HTMLDivElement>
  children?: React.ReactNode

  "data-testid"?: string
}

export const SplitButton = React.memo(
  ({
    primaryButtonRef,
    primaryButtonLabel,
    primaryButtonProps,
    onPrimaryClick,

    secondaryButtonRef,
    secondaryButtonLabel,
    secondaryButtonIcon = IconName.SMALL_CARET_DOWN,
    secondaryButtonProps,
    secondaryButtonRender,
    onSecondaryClick,

    variant = "primary",

    className,
    containerRef,
    children,
    isDisabled,
    tabIndex,
    ...rest
  }: SplitButtonProps) => {
    const { t } = useTranslation()

    const secondaryProps: ButtonProps = {
      variant,
      // Icon-only buttons have no text node, so Button's label -> aria-label
      // forwarding is the only accessible name available.
      label: secondaryButtonLabel || t("General.Expand"),
      icon: secondaryButtonIcon,
      iconSize: IconSize.XXS,
      isIconOnly: true,
      tabIndex,
      ...secondaryButtonProps,
      disabled: isDisabled || secondaryButtonProps?.disabled,
      className: classNames(
        "split-button-secondary",
        secondaryButtonProps?.className
      )
    }

    return (
      <div
        {...rest}
        ref={containerRef}
        className={classNames("split-button", className)}
      >
        <Button
          variant={variant}
          data-testid="split-button-primary"
          ref={primaryButtonRef}
          label={primaryButtonLabel}
          onClick={onPrimaryClick}
          tabIndex={tabIndex}
          {...primaryButtonProps}
          disabled={isDisabled || primaryButtonProps?.disabled}
          className={classNames(
            "split-button-primary",
            primaryButtonProps?.className
          )}
        >
          {children}
        </Button>
        {secondaryButtonRender ? (
          secondaryButtonRender(secondaryProps)
        ) : (
          <Button
            data-testid="split-button-secondary"
            ref={secondaryButtonRef}
            onClick={onSecondaryClick}
            {...secondaryProps}
          />
        )}
      </div>
    )
  }
)
